import { AgentGroupPatchDto } from "@closerplatform/spinner-openapi";
import { staticConfig } from "@fidget/common/config/static-config";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import classNames from "classnames";
import { capitalize } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import commonStyles from "../../../../../containers/settings/settingsEditCommon.css";
import { useStore } from "../../../../../models";
import { useAgentServices } from "../../../../../models/agentServices";
import settingsStyles from "../../../../../styles/settings.css";
import { SettingsForm } from "../../../settingsForm";
import { SubtitleDescription } from "../../../subtitleDescription";
import { TagsTagGroupsInput } from "../../../users/components/adviserEditScreen/tagsTagGroupsInput";
import { ExistingAgentGroupForm } from "../../types";
import { mapExistingFormToPatchForm, mapFormToAgentGroup } from "../../utils";
import { AdvisersInput } from "../advisersInput/advisersInput";
import { AdvisersList } from "../advisersList/advisersList";
import { AdviserGroupNameInput } from "../nameInput/adviserGroupNameInput";

interface Props {
  group: ExistingAgentGroupForm;
  onGoBack(): void;
  onDiscard(item: ExistingAgentGroupForm): void;
  onSave?(item: ExistingAgentGroupForm): void;
}

export const AdviserGroupEditScreen = observer(({ group, onGoBack, onSave, onDiscard }: Props) => {
  const { spinnerService } = useAgentServices();
  const { uiStore, agentStore, currentUserStore, agentGroupStore } = useStore();

  const [name] = useState(group.$.name.$);
  const [wasEdited, setWasEdited] = useState(false);

  const advisers = agentStore.getByIds(group.$.adviserIds.$);

  useEffect(() => {
    const setEdited = () => setWasEdited(true);

    group.$.name.onUpdate(setEdited);
    group.$.tagIds.onUpdate(setEdited);
    group.$.tagGroupIds.onUpdate(setEdited);
    group.$.adviserIds.onUpdate(setEdited);
  }, [group]);

  const handleDiscard = () => {
    onDiscard(group);
    setWasEdited(false);
  };

  const handleSave = async () => {
    try {
      const validate = await group.validate();
      if (validate.hasError) {
        uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.wrong"));
        return;
      }

      const updateDto = mapExistingFormToPatchForm(group);
      const permissionRestrictedUpdateDto: AgentGroupPatchDto = {
        name: uiStore.canDisplayAgentGroupsConfiguration() ? updateDto.name : undefined,
        advisers: uiStore.canDisplayAgentGroupsConfiguration() ? updateDto.advisers : undefined,
        tags: uiStore.canDisplayAdvisersAssignments() ? updateDto.tags : undefined,
        tagGroups: uiStore.canDisplayAdvisersAssignments() ? updateDto.tagGroups : undefined
      };

      await spinnerService.patchAgentGroup(permissionRestrictedUpdateDto, group.$.id.$);

      agentGroupStore.updateGroup(mapFormToAgentGroup(group, currentUserStore.orgId!));

      if (onSave) {
        onSave(group);
      }

      setWasEdited(false);
    } catch (e) {
      logger.error({ message: "Couldn't update tag group", error: e });
      uiStore.addGenericErrorToast();
    }
  };

  const handleGoBack = () => {
    if (wasEdited) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    onGoBack();
  };

  const renderTitle = () => (
    <div className={classNames(commonStyles.title, settingsStyles.pointer)} onClick={handleGoBack}>
      <i className={classNames("icon-arrow", settingsStyles.rotated)} />
      <L10n id="settings.adviserGroups.edit.title" values={{ name }} />
    </div>
  );

  const renderGroupNameSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.adviserGroups.edit.name")}
        description={capitalize(
          intl.formatMessage("settings.adviserGroups.form.group.description", {
            limit: staticConfig.agentGroupNameMaxCharacters
          })
        )}
      />
      <div className={commonStyles.content}>
        <AdviserGroupNameInput field={group.$.name} />
      </div>
    </div>
  );

  const renderGroupAdvisersSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.adviserGroups.edit.advisers")}
        description={intl.formatMessage("settings.adviserGroups.edit.advisers.description")}
      />
      <div className={commonStyles.content}>
        <AdvisersInput
          adviserIds={group.$.adviserIds.$}
          onPicked={(adviserId) => group.$.adviserIds.reset([...group.$.adviserIds.$, adviserId])}
          ListComponent={
            advisers.length ? (
              <AdvisersList
                advisers={advisers}
                onDelete={(adviser) =>
                  group.$.adviserIds.reset(group.$.adviserIds.$.filter((id) => adviser.id !== id))
                }
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );

  const renderGroupTagsSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.adviserGroups.edit.skills")}
        description={intl.formatMessage("settings.adviserGroups.edit.skills.description")}
      />
      <div className={commonStyles.content}>
        <TagsTagGroupsInput
          tagGroupIds={group.$.tagGroupIds}
          tagIds={group.$.tagIds}
          onTagDelete={(tagId) => group.$.tagIds.reset(group.$.tagIds.$.filter((t) => t !== tagId))}
          onTagGroupDelete={(tagGroupId) =>
            group.$.tagGroupIds.reset(group.$.tagGroupIds.$.filter((t) => t !== tagGroupId))
          }
        />
      </div>
    </div>
  );

  return (
    <SettingsForm disabled={!wasEdited} onDiscard={handleDiscard} onSubmit={handleSave}>
      {renderTitle()}
      {uiStore.canDisplayAgentGroupsConfiguration() && renderGroupNameSection()}
      {uiStore.canDisplayAgentGroupsConfiguration() && renderGroupAdvisersSection()}
      {uiStore.canDisplayAdvisersAssignments() && renderGroupTagsSection()}
    </SettingsForm>
  );
});
